import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import api from '../api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faStop, faFilePdf, faPlayCircle, faUpload, faList, faHistory, faChartPie, faCheckCircle, faTimesCircle, faClock, faSpinner } from '@fortawesome/free-solid-svg-icons';

const MESES = ['', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

function PanelF29Admin() {
    const [activeTab, setActiveTab] = useState('dashboard'); // dashboard, lista, historial
    const [empresas, setEmpresas] = useState([]);
    const [logs, setLogs] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedLogId, setExpandedLogId] = useState(null);
    const [selectedEmpresas, setSelectedEmpresas] = useState(new Set());
    
    // Selectores
    const [selectedMes, setSelectedMes] = useState(new Date().getMonth() || 12);
    const [selectedAnio, setSelectedAnio] = useState(selectedMes === 12 ? new Date().getFullYear() - 1 : new Date().getFullYear());
    const fileInputRef = useRef(null);

    const [isUploading, setIsUploading] = useState(false);
    const [isExecuting, setIsExecuting] = useState(false);

    useEffect(() => {
        cargarEmpresas();
    }, []);

    useEffect(() => {
        if (empresas.length > 0) {
            localStorage.setItem('selectedEmpresasF29', JSON.stringify(Array.from(selectedEmpresas)));
        }
    }, [selectedEmpresas, empresas]);

    useEffect(() => {
        cargarLogs();
        const interval = setInterval(cargarLogs, 5000); // Polling cada 5 seg
        return () => clearInterval(interval);
    }, [selectedMes, selectedAnio]);

    const cargarEmpresas = async () => {
        try {
            const res = await api.get('/f29/listado');
            setEmpresas(res.data);
            
            const saved = localStorage.getItem('selectedEmpresasF29');
            if (saved) {
                const parsed = JSON.parse(saved);
                const validIds = res.data.map(e => e._id);
                const filtered = parsed.filter(id => validIds.includes(id));
                setSelectedEmpresas(new Set(filtered));
            } else {
                setSelectedEmpresas(new Set(res.data.map(e => e._id)));
            }
        } catch (error) {
            toast.error('Error al cargar lista de empresas F29');
        }
    };

    const cargarLogs = async () => {
        if (!selectedMes || !selectedAnio) return;
        try {
            const res = await api.get(`/f29/logs/${selectedAnio}/${selectedMes}`);
            const logsMap = {};
            res.data.forEach(log => {
                logsMap[log.empresaId] = log;
            });
            setLogs(logsMap);
        } catch (error) {
            console.error('Error cargando logs F29', error);
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        setIsUploading(true);
        try {
            const res = await api.post('/f29/upload-csv', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success(res.data.message);
            cargarEmpresas();
            setActiveTab('lista');
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al subir el archivo');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const ejecutarMasivo = async () => {
        if (empresas.length === 0) return toast.warning('No hay empresas cargadas.');
        if (selectedEmpresas.size === 0) return toast.warning('Selecciona al menos una empresa.');
        if (!window.confirm(`¿Estás seguro de iniciar la declaración automática para ${selectedEmpresas.size} empresas en el período ${MESES[selectedMes]} ${selectedAnio}?`)) return;

        setIsExecuting(true);
        try {
            const res = await api.post('/f29/ejecutar', {
                mes: selectedMes,
                anio: selectedAnio,
                empresasIds: Array.from(selectedEmpresas)
            });
            toast.success(res.data.message);
            cargarLogs();
        } catch (error) {
            toast.error('Error al iniciar la ejecución masiva');
        } finally {
            setIsExecuting(false);
        }
    };

    const toggleAllEmpresas = () => {
        if (selectedEmpresas.size === empresas.length) {
            setSelectedEmpresas(new Set());
        } else {
            setSelectedEmpresas(new Set(empresas.map(e => e._id)));
        }
    };

    const toggleEmpresa = (id, event) => {
        event.stopPropagation();
        const newSet = new Set(selectedEmpresas);
        if (newSet.has(id)) {
            newSet.delete(id);
        } else {
            newSet.add(id);
        }
        setSelectedEmpresas(newSet);
    };

    const detenerEjecucion = async () => {
        if (!window.confirm(`¿Estás seguro de detener el proceso actual? Se cancelarán las tareas en cola.`)) return;
        try {
            const res = await api.post('/f29/detener');
            toast.success(res.data.message);
            cargarLogs();
            setIsExecuting(false);
        } catch (error) {
            toast.error('Error al detener la ejecución');
        }
    };

    const ejecutarIndividual = async (empresaId) => {
        try {
            await api.post('/f29/ejecutar', {
                empresasIds: [empresaId],
                mes: selectedMes,
                anio: selectedAnio
            });
            toast.success('Empresa encolada');
            cargarLogs();
        } catch (error) {
            toast.error('Error al encolar la empresa');
        }
    };

    const descargarPdf = (logId, rut) => {
        const token = localStorage.getItem('token');
        const url = `${api.defaults.baseURL}/f29/descargar-pdf/${logId}?token=${token}`;
        window.open(url, '_blank');
    };

    const getEstadoBadge = (log) => {
        if (!log || !log.estado) return <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">Pendiente</span>;
        switch (log.estado) {
            case 'en_cola': return <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800"><FontAwesomeIcon icon={faClock} className="mr-1" /> En Cola</span>;
            case 'en_progreso': return <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800"><FontAwesomeIcon icon={faSpinner} spin className="mr-1" /> Procesando</span>;
            case 'completado': 
                if (log.resultado?.type === 'ya_declarado') {
                    return <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 shadow-sm"><FontAwesomeIcon icon={faCheckCircle} className="mr-1" /> Ya Declarado</span>;
                }
                return <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800"><FontAwesomeIcon icon={faCheckCircle} className="mr-1" /> Completado</span>;
            case 'error': return <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800"><FontAwesomeIcon icon={faTimesCircle} className="mr-1" /> Error</span>;
            default: return <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">Pendiente</span>;
        }
    };

    // Calcular estadísticas para el dashboard
    const stats = { total: empresas.length, completados: 0, errores: 0, en_cola: 0, pendientes: 0 };
    empresas.forEach(emp => {
        const estado = logs[emp._id]?.estado;
        if (estado === 'completado') stats.completados++;
        else if (estado === 'error') stats.errores++;
        else if (estado === 'en_cola' || estado === 'en_progreso') stats.en_cola++;
        else stats.pendientes++;
    });

    return (
        <div className="p-4 md:p-8 max-w-7xl mx-auto bg-gray-50 min-h-screen">
            <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <div>
                    <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Módulo F29</h1>
                    <p className="text-gray-500 text-sm mt-1">Automatización de Declaraciones F29 (Sin Pago)</p>
                </div>
                
                {/* Selectores de Período Globales */}
                <div className="mt-4 md:mt-0 flex gap-3 items-center bg-gray-50 p-2 rounded-lg border border-gray-200">
                    <div>
                        <select 
                            value={selectedMes} 
                            onChange={(e) => setSelectedMes(Number(e.target.value))}
                            className="block w-full pl-3 pr-8 py-2 text-sm font-medium bg-white border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md shadow-sm"
                        >
                            {MESES.map((m, i) => i > 0 && <option key={i} value={i}>{m}</option>)}
                        </select>
                    </div>
                    <div>
                        <input 
                            type="number" 
                            value={selectedAnio} 
                            onChange={(e) => setSelectedAnio(Number(e.target.value))}
                            className="block w-20 pl-3 pr-3 py-2 text-sm font-medium bg-white border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md shadow-sm"
                        />
                    </div>
                </div>
            </div>

            {/* Pestañas de Navegación */}
            <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                    <button
                        onClick={() => setActiveTab('dashboard')}
                        className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                            activeTab === 'dashboard'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        <FontAwesomeIcon icon={faChartPie} className="mr-2" />
                        Dashboard F29
                    </button>
                    <button
                        onClick={() => setActiveTab('historial')}
                        className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                            activeTab === 'historial'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        <FontAwesomeIcon icon={faHistory} className="mr-2" />
                        Historial (Logs)
                    </button>
                </nav>
            </div>

            {/* TAB: DASHBOARD (Muestra Stats y la Lista de Empresas) */}
            {activeTab === 'dashboard' && (
                <div className="space-y-6">
                    {/* Estadísticas */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-500">Total Empresas</p>
                                <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                            </div>
                            <div className="p-3 bg-gray-100 rounded-full text-gray-600">
                                <FontAwesomeIcon icon={faList} size="lg" />
                            </div>
                        </div>
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-green-600">Completados</p>
                                <p className="text-3xl font-bold text-gray-900">{stats.completados}</p>
                            </div>
                            <div className="p-3 bg-green-100 rounded-full text-green-600">
                                <FontAwesomeIcon icon={faCheckCircle} size="lg" />
                            </div>
                        </div>
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-red-600">Errores</p>
                                <p className="text-3xl font-bold text-gray-900">{stats.errores}</p>
                            </div>
                            <div className="p-3 bg-red-100 rounded-full text-red-600">
                                <FontAwesomeIcon icon={faTimesCircle} size="lg" />
                            </div>
                        </div>
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-blue-600">En Progreso/Cola</p>
                                <p className="text-3xl font-bold text-gray-900">{stats.en_cola}</p>
                            </div>
                            <div className="p-3 bg-blue-100 rounded-full text-blue-600">
                                <FontAwesomeIcon icon={faClock} size="lg" />
                            </div>
                        </div>
                    </div>

                    {/* Upload CSV y Acciones */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="w-full md:w-1/2">
                            <label className="block text-sm font-bold text-gray-700 mb-2">Cargar Lista Maestra (CSV)</label>
                            <div className="flex items-center gap-3">
                                <label className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-200 transition-colors w-full">
                                    <FontAwesomeIcon icon={faUpload} className="text-gray-500" />
                                    <span className="text-sm font-medium text-gray-700">Seleccionar Archivo</span>
                                    <input 
                                        type="file" 
                                        accept=".csv"
                                        ref={fileInputRef}
                                        className="hidden"
                                        onChange={handleFileUpload}
                                        disabled={isUploading}
                                    />
                                </label>
                            </div>
                        </div>
                        <div className="flex gap-2 w-full md:w-auto">
                            <button 
                                onClick={ejecutarMasivo}
                                disabled={empresas.length === 0}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50 transition-all w-full md:w-auto shadow-sm"
                            >
                                <FontAwesomeIcon icon={faPlayCircle} />
                                Iniciar Proceso Masivo
                            </button>
                            <button 
                                onClick={detenerEjecucion}
                                className="bg-red-100 hover:bg-red-200 text-red-700 px-5 py-2.5 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-all w-full md:w-auto"
                            >
                                <FontAwesomeIcon icon={faStop} />
                                Detener
                            </button>
                        </div>
                    </div>

                    {/* Tabla de Resultados */}
                    <div className="bg-white shadow-sm border border-gray-200 rounded-xl overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-4 text-left">
                                            <input 
                                                type="checkbox" 
                                                checked={empresas.length > 0 && selectedEmpresas.size === empresas.length}
                                                onChange={toggleAllEmpresas}
                                                className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                            />
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">RUT</th>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Empresa</th>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Estado Actual</th>
                                        <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {empresas
                                        .sort((a, b) => a.nombre.localeCompare(b.nombre))
                                        .map(emp => {
                                        const log = logs[emp._id];
                                        return (
                                            <React.Fragment key={emp._id}>
                                                <tr 
                                                    className={`transition-colors cursor-pointer ${log?.estado === 'en_progreso' ? 'bg-yellow-50/60 hover:bg-yellow-100/60' : 'hover:bg-blue-50'}`}
                                                    onClick={() => setExpandedLogId(expandedLogId === emp._id ? null : emp._id)}
                                                >
                                                    <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                                        <input 
                                                            type="checkbox" 
                                                            checked={selectedEmpresas.has(emp._id)}
                                                            onChange={(e) => toggleEmpresa(emp._id, e)}
                                                            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                                        />
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-gray-400 text-xs w-3">{expandedLogId === emp._id ? '▼' : '▶'}</span>
                                                            {emp.rut}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{emp.nombre}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        {getEstadoBadge(log)}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={e => e.stopPropagation()}>
                                                        <div className="flex items-center justify-end gap-3">
                                                            {log?.resultado?.rutaPdf && (
                                                                <button 
                                                                    onClick={() => descargarPdf(log._id, emp.rut)}
                                                                    className="px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-md flex items-center gap-2 transition-colors border border-blue-200"
                                                                    title="Descargar Certificado"
                                                                >
                                                                    <FontAwesomeIcon icon={faFilePdf} />
                                                                    PDF
                                                                </button>
                                                            )}
                                                            {(!log || log.estado === 'error' || log.estado === 'completado') && (
                                                                <button 
                                                                    onClick={() => ejecutarIndividual(emp._id)}
                                                                    className="px-3 py-1.5 bg-gray-100 hover:bg-blue-600 hover:text-white text-gray-700 rounded-md flex items-center gap-2 transition-all font-bold text-xs"
                                                                    title="Procesar esta empresa"
                                                                >
                                                                    Procesar F29
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                                {expandedLogId === emp._id && log && (
                                                    <tr>
                                                        <td colSpan="4" className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                                                            <div className="bg-slate-900 rounded-lg p-4 shadow-inner max-h-[500px] overflow-y-auto">
                                                                <div className="flex justify-between items-center mb-3 border-b border-slate-700 pb-2">
                                                                    <h4 className="text-slate-300 text-xs font-bold uppercase tracking-wider">
                                                                        Terminal de Ejecución - {log.resultado?.mensaje || 'Detalle'}
                                                                    </h4>
                                                                    {log.resultado?.errorScreenshot && (
                                                                        <button 
                                                                            onClick={() => {
                                                                                const token = localStorage.getItem('token');
                                                                                window.open(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/f29/descargar-captura/${log._id}?token=${token}`, '_blank');
                                                                            }}
                                                                            className="bg-red-900/50 hover:bg-red-800 text-red-200 border border-red-700 px-3 py-1 rounded text-[10px] uppercase font-bold tracking-wider transition-colors"
                                                                        >
                                                                            📸 Ver Captura del Error
                                                                        </button>
                                                                    )}
                                                                </div>
                                                                {log.logEntries && log.logEntries.length > 0 ? (
                                                                    <div className="space-y-1 font-mono text-xs">
                                                                        {log.logEntries.map((entry, i) => (
                                                                            <div key={i} className={`${entry.includes('ERROR') || entry.includes('❌') ? 'text-red-400 font-bold' : entry.includes('✅') ? 'text-green-400' : 'text-gray-300'} leading-relaxed`}>{entry}</div>
                                                                        ))}
                                                                    </div>
                                                                ) : (
                                                                    <p className="text-slate-500 text-sm italic py-2">No hay logs detallados guardados para esta ejecución.</p>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                                {expandedLogId === emp._id && !log && (
                                                    <tr>
                                                        <td colSpan="4" className="px-6 py-4 bg-gray-50 border-b border-gray-200 text-center text-sm text-gray-500 italic">
                                                            No hay registros de ejecución para esta empresa en el periodo seleccionado.
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                    {empresas.length === 0 && (
                                        <tr>
                                            <td colSpan="4" className="px-6 py-16 text-center">
                                                <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                                    <FontAwesomeIcon icon={faList} className="text-gray-400 text-xl" />
                                                </div>
                                                <p className="text-gray-500 font-medium">No hay empresas cargadas en la base de datos.</p>
                                                <p className="text-gray-400 text-sm mt-1">Sube un archivo CSV usando el panel superior para comenzar.</p>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB: HISTORIAL */}
            {activeTab === 'historial' && (
                <div className="bg-white shadow-sm border border-gray-200 rounded-xl overflow-hidden flex flex-col h-full">
                    <div className="p-5 bg-white border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h3 className="text-lg font-bold text-gray-900">Registro Histórico ({MESES[selectedMes]} {selectedAnio})</h3>
                            <p className="text-sm text-gray-500 mt-1">Bitácora detallada de ejecuciones y errores reportados por el robot.</p>
                        </div>
                        <div className="flex items-center gap-3 w-full sm:w-auto">
                            <input
                                type="text"
                                placeholder="Buscar por RUT o Nombre..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="block w-full sm:w-64 pl-3 pr-3 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md"
                            />
                            <button 
                                onClick={cargarLogs} 
                                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap"
                            >
                                <FontAwesomeIcon icon={faHistory} className="mr-2" />
                                Refrescar
                            </button>
                        </div>
                    </div>
                    <div className="overflow-x-auto max-h-[700px] overflow-y-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0 shadow-sm z-10">
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Fecha / Hora</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Empresa (RUT)</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Estado</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Tipo Declaración</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Diagnóstico / Mensaje</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                                {Object.values(logs)
                                    .filter(log => selectedEmpresas.has(log.empresaId))
                                    .filter(log => !searchTerm || log.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) || log.rut?.includes(searchTerm))
                                    .sort((a, b) => {
                                        const getPriority = log => {
                                            if (log.estado === 'en_progreso') return 1;
                                            if (log.estado === 'en_cola') return 2;
                                            if (log.estado === 'error') return 3;
                                            return 4;
                                        };
                                        const pA = getPriority(a);
                                        const pB = getPriority(b);
                                        if (pA !== pB) return pA - pB;
                                        return (a.nombre || a.empresaId || '').localeCompare(b.nombre || b.empresaId || '');
                                    })
                                    .map((log, index) => (
                                    <React.Fragment key={index}>
                                        <tr 
                                            className="hover:bg-blue-50/50 transition-colors cursor-pointer"
                                            onClick={() => setExpandedLogId(expandedLogId === log._id ? null : log._id)}
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {log.updatedAt ? new Date(log.updatedAt).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}
                                            </td>
                                            <td className="px-6 py-4 text-sm font-medium text-gray-900">
                                                <div className="flex flex-col">
                                                    <span>{log.nombre || log.empresaId}</span>
                                                    <span className="text-xs text-gray-500 font-normal">{log.rut}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {getEstadoBadge(log)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {log.resultado?.type ? (
                                                    <span className="px-2.5 py-1 text-xs font-semibold rounded-md border bg-gray-50 border-gray-200 text-gray-600">
                                                        {log.resultado.type.replace('_', ' ').toUpperCase()}
                                                    </span>
                                                ) : <span className="text-gray-400 text-xs">-</span>}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600 break-words max-w-md">
                                                <div className={`p-2 rounded-md flex justify-between items-center ${log.estado === 'error' ? 'bg-red-50 text-red-800 border border-red-100' : 'bg-gray-50'}`}>
                                                    <span>{log.resultado?.mensaje || (log.estado === 'en_cola' ? 'Esperando turno de ejecución...' : 'Sin detalles')}</span>
                                                    <span className="text-xs text-gray-400 ml-2">{expandedLogId === log._id ? '▲' : '▼'}</span>
                                                </div>
                                            </td>
                                        </tr>
                                        {expandedLogId === log._id && (
                                            <tr>
                                                <td colSpan="5" className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                                                    <div className="bg-slate-900 rounded-lg p-4 shadow-inner max-h-[500px] overflow-y-auto">
                                                        <div className="flex justify-between items-center mb-3 border-b border-slate-700 pb-2">
                                                            <h4 className="text-slate-300 text-xs font-bold uppercase tracking-wider">Terminal de Ejecución</h4>
                                                            {log.resultado?.errorScreenshot && (
                                                                <button 
                                                                    onClick={() => {
                                                                        const token = localStorage.getItem('token');
                                                                        window.open(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/f29/descargar-captura/${log._id}?token=${token}`, '_blank');
                                                                    }}
                                                                    className="bg-red-900/50 hover:bg-red-800 text-red-200 border border-red-700 px-3 py-1 rounded text-[10px] uppercase font-bold tracking-wider transition-colors"
                                                                >
                                                                    📸 Ver Captura del Error
                                                                </button>
                                                            )}
                                                        </div>
                                                        {log.logEntries && log.logEntries.length > 0 ? (
                                                            <div className="space-y-1 font-mono text-xs">
                                                                {log.logEntries.map((entry, i) => (
                                                                    <div key={i} className={`${entry.includes('ERROR') || entry.includes('❌') ? 'text-red-400 font-bold' : entry.includes('✅') ? 'text-green-400' : 'text-gray-300'} leading-relaxed`}>{entry}</div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <p className="text-slate-500 text-sm italic py-2">No hay logs de terminal guardados para esta ejecución.</p>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                                {Object.values(logs).length === 0 && (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-16 text-center">
                                            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                                <FontAwesomeIcon icon={faHistory} className="text-gray-400 text-xl" />
                                            </div>
                                            <p className="text-gray-500 font-medium">No hay registros históricos para este período.</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default PanelF29Admin;
